import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, addDoc, doc, runTransaction, 
  Timestamp, query, where, orderBy 
} from 'firebase/firestore';

export const OrderCreation = ({ onClose, onSuccess }) => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientType, setClientType] = useState('existing');
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [orderItems, setOrderItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);

  useEffect(() => {
    fetchClients();
    fetchProducts();
  }, []);

  useEffect(() => {
    const filtered = products.filter(product => 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.modelNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setAvailableProducts(filtered);
  }, [searchTerm, products]);

  const fetchClients = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      const snapshot = await getDocs(clientsRef);
      const clientsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      const productsList = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const product = { id: doc.id, ...doc.data() };
          const bomQuery = query(collection(db, 'bom'), where('productId', '==', product.id));
          const bomSnapshot = await getDocs(bomQuery);
          const bomItems = bomSnapshot.docs.map(bomDoc => bomDoc.data());
          const totalCost = bomItems.reduce((sum, item) => sum + (item.totalCost || 0), 0);
          return { ...product, totalCost };
        })
      );
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const createNewClient = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      const docRef = await addDoc(clientsRef, {
        name: newClientData.name,
        email: newClientData.email || '',
        phone: newClientData.phone || '',
        address: newClientData.address || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  };

  const addProductToOrder = (product) => {
    const existingItem = orderItems.find(item => item.productId === product.id);
    
    if (existingItem) {
      setOrderItems(orderItems.map(item =>
        item.productId === product.id
          ? { ...item, quantity: item.quantity + 1, totalSellingPrice: (item.quantity + 1) * item.unitSellingPrice, totalCOGS: (item.quantity + 1) * item.unitCOGS }
          : item
      ));
    } else {
      setOrderItems([
        ...orderItems,
        {
          productId: product.id,
          productName: product.name,
          modelNumber: product.modelNumber,
          quantity: 1,
          unitSellingPrice: product.sellingPrice,
          totalSellingPrice: product.sellingPrice,
          unitCOGS: product.totalCost || 0,
          totalCOGS: product.totalCost || 0
        }
      ]);
    }
    setShowProductSelector(false);
    setSearchTerm('');
  };

  const updateQuantity = (index, quantity) => {
    const updated = [...orderItems];
    const qty = parseInt(quantity) || 0;
    if (qty <= 0) {
      removeItem(index);
      return;
    }
    updated[index].quantity = qty;
    updated[index].totalSellingPrice = qty * updated[index].unitSellingPrice;
    updated[index].totalCOGS = qty * updated[index].unitCOGS;
    setOrderItems(updated);
  };

  const removeItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const totalSelling = orderItems.reduce((sum, item) => sum + (item.totalSellingPrice || 0), 0);
    const totalCOGS = orderItems.reduce((sum, item) => sum + (item.totalCOGS || 0), 0);
    const grossProfit = totalSelling - totalCOGS;
    const margin = totalSelling > 0 ? (grossProfit / totalSelling * 100).toFixed(1) : 0;
    return { totalSelling, totalCOGS, grossProfit, margin };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let clientId = selectedClient;
    
    if (clientType === 'new') {
      if (!newClientData.name.trim()) {
        alert('Please enter client name');
        return;
      }
      try {
        clientId = await createNewClient();
      } catch (error) {
        alert('Error creating client. Please try again.');
        return;
      }
    } else if (!clientId) {
      alert('Please select a client');
      return;
    }
    
    if (orderItems.length === 0) {
      alert('Please add at least one product');
      return;
    }
    
    setLoading(true);
    
    try {
      const { totalSelling, totalCOGS } = calculateTotals();
      const timestamp = Timestamp.now();
      const orderNumber = `INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
      
      await runTransaction(db, async (transaction) => {
        // Create order
        const orderRef = doc(collection(db, 'orders'));
        const orderData = {
          orderId: orderRef.id,
          orderNumber: orderNumber,
          clientId: clientId,
          orderDate: timestamp,
          status: 'deposit',
          items: orderItems.map(item => ({
            productId: item.productId,
            productName: item.productName,
            modelNumber: item.modelNumber,
            quantity: item.quantity,
            unitSellingPrice: item.unitSellingPrice,
            totalSellingPrice: item.totalSellingPrice,
            unitCOGS: item.unitCOGS,
            totalCOGS: item.totalCOGS
          })),
          totalSellingPrice: totalSelling,
          totalCOGS: totalCOGS,
          balanceDue: totalSelling,
          createdAt: timestamp,
          updatedAt: timestamp
        };
        transaction.set(orderRef, orderData);
        
        // Create order items
        for (const item of orderItems) {
          const itemRef = doc(collection(db, 'orderItems'));
          transaction.set(itemRef, {
            orderItemId: itemRef.id,
            orderId: orderRef.id,
            productId: item.productId,
            productName: item.productName,
            quantity: item.quantity,
            unitSellingPrice: item.unitSellingPrice,
            totalSellingPrice: item.totalSellingPrice,
            unitCOGS: item.unitCOGS,
            totalCOGS: item.totalCOGS,
            createdAt: timestamp
          });
        }
        
        // Create journal entry
        const journalRef = doc(collection(db, 'journalEntries'));
        transaction.set(journalRef, {
          journalId: journalRef.id,
          transactionId: orderRef.id,
          transactionType: 'sale',
          date: timestamp,
          entries: [
            {
              account: 'accounts_receivable',
              accountName: 'Accounts Receivable',
              debit: totalSelling,
              credit: 0,
              description: `Sale - ${orderNumber}`
            },
            {
              account: 'sales_revenue',
              accountName: 'Sales Revenue',
              debit: 0,
              credit: totalSelling,
              description: `Sale - ${orderNumber}`
            }
          ],
          createdAt: timestamp,
          createdBy: 'system'
        });
      });
      
      alert('Order created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating order: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const { totalSelling, totalCOGS, grossProfit, margin } = calculateTotals();

  return React.createElement(
    'div',
    { style: modalStyles.overlay, onClick: onClose },
    React.createElement(
      'div',
      { style: { ...modalStyles.container, maxWidth: '800px' }, onClick: (e) => e.stopPropagation() },
      React.createElement(
        'div',
        { style: modalStyles.header },
        React.createElement('h2', { style: modalStyles.title }, 'Create New Order'),
        React.createElement('button', { style: modalStyles.closeButton, onClick: onClose }, '×')
      ),
      React.createElement(
        'form',
        { onSubmit: handleSubmit, style: modalStyles.form },
        
        React.createElement(
          'div',
          { style: orderStyles.clientTypeToggle },
          React.createElement(
            'button',
            {
              type: 'button',
              style: {
                ...orderStyles.toggleButton,
                ...(clientType === 'existing' ? orderStyles.toggleButtonActive : {})
              },
              onClick: () => setClientType('existing')
            },
            'Existing Client'
          ),
          React.createElement(
            'button',
            {
              type: 'button',
              style: {
                ...orderStyles.toggleButton,
                ...(clientType === 'new' ? orderStyles.toggleButtonActive : {})
              },
              onClick: () => setClientType('new')
            },
            'New Client'
          )
        ),
        
        clientType === 'existing' && React.createElement(
          'div',
          { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Select Client *'),
          React.createElement(
            'select',
            {
              style: modalStyles.select,
              value: selectedClient,
              onChange: (e) => setSelectedClient(e.target.value),
              required: true
            },
            React.createElement('option', { value: '' }, 'Choose a client...'),
            clients.map(client =>
              React.createElement('option', { key: client.id, value: client.id },
                `${client.name} ${client.email ? `- ${client.email}` : ''}`
              )
            )
          )
        ),
        
        clientType === 'new' && React.createElement(
          'div',
          { style: orderStyles.newClientForm },
          React.createElement('h4', { style: orderStyles.newClientTitle }, 'New Client Information'),
          React.createElement(
            'div',
            { style: modalStyles.formGroup },
            React.createElement('label', { style: modalStyles.label }, 'Client Name *'),
            React.createElement('input', {
              type: 'text',
              style: modalStyles.input,
              value: newClientData.name,
              onChange: (e) => setNewClientData({ ...newClientData, name: e.target.value }),
              placeholder: 'Enter client name',
              required: true
            })
          ),
          React.createElement(
            'div',
            { style: modalStyles.formGroup },
            React.createElement('label', { style: modalStyles.label }, 'Email'),
            React.createElement('input', {
              type: 'email',
              style: modalStyles.input,
              value: newClientData.email,
              onChange: (e) => setNewClientData({ ...newClientData, email: e.target.value }),
              placeholder: 'client@example.com'
            })
          ),
          React.createElement(
            'div',
            { style: modalStyles.formGroup },
            React.createElement('label', { style: modalStyles.label }, 'Phone'),
            React.createElement('input', {
              type: 'tel',
              style: modalStyles.input,
              value: newClientData.phone,
              onChange: (e) => setNewClientData({ ...newClientData, phone: e.target.value }),
              placeholder: '+256 XXX XXX XXX'
            })
          ),
          React.createElement(
            'div',
            { style: modalStyles.formGroup },
            React.createElement('label', { style: modalStyles.label }, 'Address'),
            React.createElement('textarea', {
              style: { ...modalStyles.input, ...orderStyles.textarea },
              value: newClientData.address,
              onChange: (e) => setNewClientData({ ...newClientData, address: e.target.value }),
              placeholder: 'Client address',
              rows: '2'
            })
          )
        ),
        
        React.createElement(
          'div',
          { style: orderStyles.itemsSection },
          React.createElement(
            'div',
            { style: orderStyles.itemsHeader },
            React.createElement('h3', { style: orderStyles.itemsTitle }, 'Order Items'),
            React.createElement(
              'button',
              {
                type: 'button',
                style: orderStyles.addButton,
                onClick: () => setShowProductSelector(true)
              },
              '+ Add Product'
            )
          ),
          
          orderItems.length > 0 ? React.createElement(
            'div',
            { style: orderStyles.tableWrapper },
            React.createElement(
              'table',
              { style: orderStyles.table },
              React.createElement(
                'thead',
                null,
                React.createElement(
                  'tr',
                  { style: orderStyles.tableHeaderRow },
                  React.createElement('th', { style: orderStyles.tableHeader }, 'Product'),
                  React.createElement('th', { style: orderStyles.tableHeader }, 'Model'),
                  React.createElement('th', { style: orderStyles.tableHeader }, 'Quantity'),
                  React.createElement('th', { style: orderStyles.tableHeader }, 'Unit Price'),
                  React.createElement('th', { style: orderStyles.tableHeader }, 'Total'),
                  React.createElement('th', { style: orderStyles.tableHeader }, '')
                )
              ),
              React.createElement(
                'tbody',
                null,
                orderItems.map((item, index) =>
                  React.createElement(
                    'tr',
                    { key: index, style: orderStyles.tableRow },
                    React.createElement('td', { style: orderStyles.tableCell }, item.productName),
                    React.createElement('td', { style: orderStyles.tableCell }, item.modelNumber),
                    React.createElement(
                      'td',
                      { style: orderStyles.tableCell },
                      React.createElement('input', {
                        type: 'number',
                        min: '1',
                        value: item.quantity,
                        onChange: (e) => updateQuantity(index, e.target.value),
                        style: orderStyles.quantityInput
                      })
                    ),
                    React.createElement('td', { style: orderStyles.tableCell }, item.unitSellingPrice.toLocaleString()),
                    React.createElement('td', { style: orderStyles.tableCell }, item.totalSellingPrice.toLocaleString()),
                    React.createElement(
                      'td',
                      { style: orderStyles.tableCell },
                      React.createElement(
                        'button',
                        {
                          type: 'button',
                          style: orderStyles.removeButton,
                          onClick: () => removeItem(index)
                        },
                        '🗑️'
                      )
                    )
                  )
                )
              )
            ),
            React.createElement(
              'div',
              { style: orderStyles.summary },
              React.createElement(
                'div',
                { style: orderStyles.summaryRow },
                React.createElement('span', null, 'Subtotal:'),
                React.createElement('span', null, `${totalSelling.toLocaleString()} UGX`)
              ),
              React.createElement(
                'div',
                { style: orderStyles.summaryRow },
                React.createElement('span', null, 'Total COGS:'),
                React.createElement('span', { style: orderStyles.costColor }, `${totalCOGS.toLocaleString()} UGX`)
              ),
              React.createElement(
                'div',
                { style: orderStyles.summaryRow },
                React.createElement('span', null, 'Gross Profit:'),
                React.createElement('span', { style: orderStyles.profitColor }, `${grossProfit.toLocaleString()} UGX`)
              ),
              React.createElement(
                'div',
                { style: orderStyles.summaryRow },
                React.createElement('span', null, 'Margin:'),
                React.createElement('span', { style: orderStyles.marginColor }, `${margin}%`)
              )
            )
          ) : React.createElement(
            'div',
            { style: orderStyles.emptyItems },
            React.createElement('p', null, 'No products added yet'),
            React.createElement(
              'button',
              {
                type: 'button',
                style: orderStyles.linkButton,
                onClick: () => setShowProductSelector(true)
              },
              'Add your first product →'
            )
          )
        ),
        
        React.createElement(
          'div',
          { style: modalStyles.actions },
          React.createElement(
            'button',
            { type: 'button', style: modalStyles.buttonSecondary, onClick: onClose },
            'Cancel'
          ),
          React.createElement(
            'button',
            { type: 'submit', style: modalStyles.buttonPrimary, disabled: loading },
            loading ? 'Creating Order...' : 'Create Order'
          )
        )
      ),
      
      showProductSelector && React.createElement(ProductSelectorModal, {
        products: availableProducts,
        searchTerm: searchTerm,
        setSearchTerm: setSearchTerm,
        onSelect: addProductToOrder,
        onClose: () => {
          setShowProductSelector(false);
          setSearchTerm('');
        }
      })
    )
  );
};

const ProductSelectorModal = ({ products, searchTerm, setSearchTerm, onSelect, onClose }) => {
  return React.createElement(
    'div',
    { style: modalStyles.overlay, onClick: onClose },
    React.createElement(
      'div',
      { style: { ...modalStyles.container, maxWidth: '500px' }, onClick: (e) => e.stopPropagation() },
      React.createElement(
        'div',
        { style: modalStyles.header },
        React.createElement('h2', { style: modalStyles.title }, 'Select Product'),
        React.createElement('button', { style: modalStyles.closeButton, onClick: onClose }, '×')
      ),
      React.createElement(
        'div',
        { style: modalStyles.form },
        React.createElement(
          'div',
          { style: modalStyles.formGroup },
          React.createElement('input', {
            type: 'text',
            style: modalStyles.input,
            placeholder: 'Search by name or model...',
            value: searchTerm,
            onChange: (e) => setSearchTerm(e.target.value),
            autoFocus: true
          })
        ),
        React.createElement(
          'div',
          { style: selectorStyles.list },
          products.map(product =>
            React.createElement(
              'div',
              {
                key: product.id,
                style: selectorStyles.item,
                onClick: () => onSelect(product)
              },
              React.createElement(
                'div',
                null,
                React.createElement('div', { style: selectorStyles.itemName }, product.name),
                React.createElement('div', { style: selectorStyles.itemModel }, product.modelNumber)
              ),
              React.createElement('div', { style: selectorStyles.itemPrice }, `${product.sellingPrice.toLocaleString()} UGX`)
            )
          ),
          products.length === 0 && React.createElement(
            'div',
            { style: selectorStyles.empty },
            'No products found. Create a product first.'
          )
        )
      )
    )
  );
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  container: {
    background: '#ffffff',
    borderRadius: '24px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eef2f6'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0f172a'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#94a3b8'
  },
  form: {
    padding: '24px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    background: '#ffffff',
    cursor: 'pointer'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  buttonPrimary: {
    flex: 1,
    background: '#7f1a2e',
    color: '#ffffff',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  buttonSecondary: {
    flex: 1,
    background: '#f1f5f9',
    color: '#334155',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

const orderStyles = {
  clientTypeToggle: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    background: '#f1f5f9',
    padding: '4px',
    borderRadius: '12px'
  },
  toggleButton: {
    flex: 1,
    padding: '10px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    background: 'transparent',
    color: '#64748b',
    transition: 'all 0.2s ease'
  },
  toggleButtonActive: {
    background: '#ffffff',
    color: '#7f1a2e',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  newClientForm: {
    background: '#f8fafc',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '1px solid #eef2f6'
  },
  newClientTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px'
  },
  textarea: {
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  itemsSection: {
    marginBottom: '24px'
  },
  itemsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  itemsTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b'
  },
  addButton: {
    background: '#f1f5f9',
    color: '#475569',
    padding: '6px 14px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    cursor: 'pointer'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px',
    border: '1px solid #eef2f6'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  tableHeaderRow: {
    background: '#f8fafc',
    borderBottom: '1px solid #eef2f6'
  },
  tableHeader: {
    textAlign: 'left',
    padding: '12px 12px',
    fontWeight: '600',
    color: '#475569'
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9'
  },
  tableCell: {
    padding: '12px 12px',
    color: '#334155'
  },
  quantityInput: {
    width: '70px',
    padding: '6px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    textAlign: 'center',
    fontSize: '13px'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    color: '#94a3b8'
  },
  summary: {
    padding: '16px',
    background: '#f8fafc',
    textAlign: 'right',
    borderTop: '1px solid #eef2f6'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    color:'black',
    padding: '4px 0',
    fontSize: '13px'
  },
  costColor: {
    color: '#f59e0b',
    fontWeight: '500'
  },
  profitColor: {
    color: '#10b981',
    fontWeight: '500'
  },
  marginColor: {
    color: '#3b82f6',
    fontWeight: '500'
  },
  emptyItems: {
    textAlign: 'center',
    padding: '40px',
    background: '#f8fafc',
    borderRadius: '12px',
    color: '#94a3b8'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#7f1a2e',
    cursor: 'pointer',
    marginTop: '8px',
    fontSize: '12px',
    textDecoration: 'underline'
  }
};

const selectorStyles = {
  list: {
    maxHeight: '400px',
    overflowY: 'auto'
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    border: '1px solid #eef2f6',
    borderRadius: '10px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  itemName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1e293b'
  },
  itemModel: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '2px'
  },
  itemPrice: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#7f1a2e'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: '#94a3b8'
  }
};

export default OrderCreation;